/* =========================================================
   Dashboard — three views.
     A "Clásica"   classic KPI grid + lists
     B "Resumen"   editorial / call-to-action
     C "Operativa" data-dense workspace (table + side rail)
   Site Manager only gets B and C.
   ========================================================= */
import React from 'react';
import type { Role, SessionUser, Site, WindowState } from '@/types';
import { ic, sized } from '@/components/Icons';
import { DataTable, KPI, PageHeader, ProgressBar, VariantSwitch, type Column } from '@/components/ui';
import { COUNTRIES, ds, userOrgIds } from '@/data';
import { useI18n } from '@/i18n';
import { completeness, siteSchema } from '@/lib/fields';
import { progresoSite } from '@/lib/progresoPortal';
import { siteHasRed, useScopedData, type ScopedData } from '@/hooks/useScopedData';
import { useGo, type GoFn } from '@/hooks/useRoute';
import { usePuedeEditar } from '@/hooks/usePuedeEditar';
import { useAppStore } from '@/store/useAppStore';

interface ViewProps {
  role: Role;
  user: SessionUser;
  go: GoFn;
  stats: ScopedData;
  windowState: WindowState;
}

/* ---------- Variant A: classic ---------- */

function VariantA({ role, go, stats, windowState }: ViewProps) {
  const { lang, t } = useI18n();

  const orgColumns: Column<Record<string, unknown>>[] = [
    {
      key: 'OrgName',
      label: lang === 'es' ? 'Organización' : 'Organization',
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{String(r.OrgName)}</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{String(r.OrgId)}</div>
        </div>
      ),
    },
    {
      key: 'country',
      label: lang === 'es' ? 'País' : 'Country',
      render: (r) => {
        const c = COUNTRIES.find((x) => x.code === r.OrgCountry);
        return c ? (lang === 'es' ? c.name_es : c.name_en) : String(r.OrgCountry ?? '');
      },
    },
    { key: 'sites', label: 'Sites', align: 'right', render: (r) => <span className="mono">{ds.listSites({ orgId: String(r.OrgId) }).length}</span> },
    { key: 'instr', label: lang === 'es' ? 'Instructores' : 'Instructors', align: 'right', render: (r) => <span className="mono">{ds.listAssignments({ orgId: String(r.OrgId) }).length}</span> },
    { key: 'year', label: lang === 'es' ? 'Año' : 'Year', align: 'right', mono: true, render: (r) => String(r.YearJoined ?? '') },
    { key: 'go', label: '', align: 'right', render: () => <span style={{ color: 'var(--text-3)' }}>{ic.chevR}</span> },
  ];

  const pendientesLegal = ds.listLegalRequests().filter((r) => r.status === 'pending').length;

  const alerts = [
    {
      kind: 'info' as const,
      icon: ic.bell,
      title: t(windowState.open ? 'window.open_banner' : 'window.closed_banner', {
        date: new Date(windowState.deadline).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US'),
      }),
    },
    role === 'ETC_ADMIN'
      ? { kind: 'warn' as const, icon: ic.warn, title: `${stats.disassociated} ${lang === 'es' ? 'instructores desasociados por revisar' : 'disassociated instructors to review'}` }
      : null,
    { kind: 'err' as const, icon: ic.warn, title: `${stats.sitesWithRed} ${lang === 'es' ? 'sites tienen campos rojos pendientes' : 'sites have red fields pending'}` },
    // El «2» estaba escrito a mano: decía dos solicitudes pendientes con el
    // portal recién vaciado y sin ninguna. Las otras tres alertas de esta
    // misma lista cuentan de verdad; esta se quedó de la maqueta. Contarlas
    // es lo que ya hace el bloque de solicitudes más abajo en este archivo.
    role === 'ETC_ADMIN'
      ? {
        kind: 'info' as const,
        icon: ic.mail,
        title: `${pendientesLegal} ${lang === 'es'
          ? 'solicitudes de cambio de contacto legal pendientes'
          : 'legal contact change requests pending'}`,
      }
      : null,
  ].filter(Boolean) as { kind: 'info' | 'warn' | 'err'; icon: typeof ic.bell; title: string }[];

  const toneOf = (k: 'info' | 'warn' | 'err') =>
    k === 'err' ? 'var(--err)' : k === 'warn' ? 'var(--warn)' : 'var(--brand-primary)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        <KPI label={t('kpi.orgs')} value={stats.orgs.length} icon={ic.org} sub={lang === 'es' ? 'En tu alcance' : 'Within your scope'} onClick={() => go('org')} />
        <KPI label={t('kpi.sites')} value={stats.sites.length} icon={ic.site} onClick={() => go('sites')} />
        <KPI label={t('kpi.active_instr')} value={stats.instr.filter((i) => !i.disassociated).length} icon={ic.instr} onClick={() => go('instructors')} />
        {role === 'ETC_ADMIN' ? (
          <KPI label={t('kpi.orphans')} value={stats.disassociated} icon={ic.warn} accent="var(--warn)" sub={lang === 'es' ? 'Sin afiliación' : 'No affiliation'} onClick={() => go('instructors')} />
        ) : null}
        <KPI label={t('kpi.red_pending')} value={stats.sitesWithRed} icon={ic.warn} accent="var(--err)" onClick={() => go('sites')} />
      </div>

      {/* row: sites needing attention + alerts */}
      <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {lang === 'es' ? 'Sites con campos obligatorios pendientes' : 'Sites with pending required fields'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                {lang === 'es' ? 'Completa estos campos antes de que cierre el proceso.' : 'Complete before the process closes.'}
              </div>
            </div>
            <button className="btn sm" onClick={() => go('sites')}>
              {lang === 'es' ? 'Ver todos' : 'View all'} {ic.chevR}
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {stats.sites.filter(siteHasRed).slice(0, 5).map((s) => {
              const avance = progresoSite(s.SiteId);
              const missing: string[] = [];
              if (!s.CommercialSiteName) missing.push(lang === 'es' ? 'Nombre comercial' : 'Commercial name');
              if (!s.Workstations) missing.push(lang === 'es' ? 'Estaciones' : 'Workstations');
              if (!s.RegisteredPostalCode) missing.push(lang === 'es' ? 'CP registrado' : 'Registered postal');
              if (s.SitePostalCode === '0000') missing.push(lang === 'es' ? 'CP "0000"' : 'Postal "0000"');
              return (
                <button
                  key={s.SiteId}
                  onClick={() => go('site', { siteId: s.SiteId })}
                  className="list-row"
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 110px', gap: 10,
                    padding: '12px 16px', borderBottom: '1px solid var(--line)',
                    background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer',
                    borderLeft: '3px solid var(--err)', borderRadius: 0,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{s.SiteName}</div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{s.SiteId} · {s.OrgName}</div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {missing.map((m, i) => (
                      <span key={i} className="chip err">{m}</span>
                    ))}
                  </div>
                  <div><ProgressBar pct={avance.pct} /></div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{lang === 'es' ? 'Alertas y avisos' : 'Alerts & notices'}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {alerts.map((a, i) => (
              <div
                key={i}
                style={{
                  display: 'flex', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--line)',
                  borderLeft: '3px solid ' + toneOf(a.kind),
                }}
              >
                <span style={{ color: toneOf(a.kind), flexShrink: 0 }}>{sized(a.icon, 16)}</span>
                <div style={{ fontSize: 13, color: 'var(--text)' }}>{a.title}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* row: orgs list */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
            {lang === 'es' ? 'Organizaciones en tu alcance' : 'Organizations in your scope'}
          </h3>
          <button className="btn sm ghost" onClick={() => go('org')}>
            {lang === 'es' ? 'Ver todas' : 'View all'} {ic.chevR}
          </button>
        </div>
        <DataTable
          columns={orgColumns}
          rows={stats.orgs.slice(0, 8).map((o) => ({ ...o, _key: o.OrgId }))}
          onRowClick={(o) => go('org', { orgId: String(o.OrgId) })}
        />
      </div>
    </div>
  );
}

/* ---------- Variant B: editorial ---------- */

function VariantB({ user, go, stats, windowState }: ViewProps) {
  const { lang, t } = useI18n();
  const greeting = (() => {
    const h = new Date().getHours();
    if (lang === 'es') return h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches';
    return h < 12 ? 'Good morning' : h < 19 ? 'Good afternoon' : 'Good evening';
  })();
  const multi = userOrgIds(user).length > 1;
  /* El saludo decía «Estás en modo solo lectura. La próxima ventana abrirá
     pronto» a todo el mundo con la ventana cerrada, igual que hacía la
     franja. Al Admin ETC eso es falso —escribe siempre— y al Site Manager
     le promete una próxima ventana en la que tampoco podrá editar, porque
     su rol es de solo consulta. Se le pregunta a la misma regla. */
  const permiso = usePuedeEditar('site');
  const exento = !windowState.open && permiso.puede;
  const soloConsulta = permiso.permanente;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* hero */}
      <div
        className="card m-grid1"
        style={{
          padding: 24, background: 'linear-gradient(120deg, #FFFFFF 0%, #FBF6F1 70%, #FDEDE3 100%)',
          display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 24, position: 'relative', overflow: 'hidden',
        }}
      >
        <svg style={{ position: 'absolute', right: -40, top: -40, opacity: 0.25 }} width="320" height="320" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="48" stroke="#F26522" strokeWidth="0.3" fill="none" />
          <circle cx="50" cy="50" r="34" stroke="#F26522" strokeWidth="0.3" fill="none" />
          <circle cx="50" cy="50" r="20" stroke="#F26522" strokeWidth="0.3" fill="none" />
        </svg>
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
            {greeting}, {user.name.split(' ')[0]}
          </div>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', maxWidth: 540 }}>
            {soloConsulta
              ? lang === 'es'
                ? 'Tu cuenta es de solo consulta. Puedes ver todo, sin cambiarlo.'
                : 'Your account is read-only. You can see everything, without changing it.'
              : exento
                ? lang === 'es'
                  ? 'El proceso está cerrado para los socios. Tú sí puedes editar.'
                  : 'The process is closed for partners. You can still edit.'
                : windowState.open
                  ? lang === 'es'
                    ? 'El proceso de actualización está abierto. Revisa y completa tus fichas.'
                    : 'The update process is open. Review and complete your records.'
                  : lang === 'es'
                    ? 'Estás en modo solo lectura. El próximo proceso abrirá pronto.'
                    : 'You are in read-only mode. The next process opens soon.'}
          </h2>
          <p style={{ color: 'var(--text-2)', fontSize: 14, lineHeight: 1.55, marginTop: 10, maxWidth: 540 }}>
            {soloConsulta
              ? lang === 'es'
                ? 'Para cambiar datos hace falta una cuenta con permiso de edición.'
                : 'Changing data requires an account with editing permission.'
              : exento
                ? lang === 'es'
                  ? `${stats.sitesWithRed} sites tienen campos obligatorios pendientes.`
                  : `${stats.sitesWithRed} sites have required fields pending.`
                : windowState.open
                  ? lang === 'es'
                    ? `Cierra el ${new Date(windowState.deadline).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}. ${stats.sitesWithRed} sites necesitan atención inmediata.`
                    : `Closes ${new Date(windowState.deadline).toLocaleDateString('en-US', { day: 'numeric', month: 'long' })}. ${stats.sitesWithRed} sites need immediate attention.`
                  : lang === 'es'
                    ? 'Puedes consultar todos los datos sin editarlos.'
                    : 'You can consult all data without editing.'}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            {/* El botón sigue al permiso, no a la ventana. Así el Admin ETC
                lo tiene con la ventana cerrada —puede editar— y el Site
                Manager no lo tiene nunca, ni con la ventana abierta. */}
            {permiso.puede ? (
              <button className="btn primary" onClick={() => go('sites')}>
                {ic.edit} {lang === 'es' ? 'Completar fichas' : 'Complete records'}
              </button>
            ) : null}
            <button className="btn" onClick={() => go('org')}>
              {ic.org}{' '}
              {lang === 'es'
                ? multi ? 'Mis organizaciones' : 'Mi organización'
                : multi ? 'My organizations' : 'My organization'}
            </button>
          </div>
        </div>

        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 14, justifyContent: 'center' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <KPI label={t('kpi.sites')} value={stats.sites.length} icon={ic.site} onClick={() => go('sites')} />
            <KPI label={t('kpi.active_instr')} value={stats.instr.filter((i) => !i.disassociated).length} icon={ic.instr} onClick={() => go('instructors')} />
          </div>
        </div>
      </div>

      {/* recommended actions */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{lang === 'es' ? 'Acciones recomendadas' : 'Recommended actions'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
              {lang === 'es' ? 'Priorizado por urgencia y progreso' : 'Sorted by urgency and completeness'}
            </div>
          </div>
          <button className="btn sm" onClick={() => go('sites')}>
            {lang === 'es' ? 'Ver todos' : 'View all'} {ic.chevR}
          </button>
        </div>
        <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
          {stats.sites.filter(siteHasRed).slice(0, 6).map((s, idx) => {
            // dos números distintos: la etiqueta cuenta los rojos que faltan,
            // la barra mide el avance del proceso sobre todo lo del site
            const rojos = completeness(siteSchema, s);
            const avance = progresoSite(s.SiteId);
            return (
              <button
                key={s.SiteId}
                onClick={() => go('site', { siteId: s.SiteId })}
                className="list-row"
                style={{
                  padding: '16px 18px', borderBottom: '1px solid var(--line)',
                  borderRight: idx % 2 === 0 ? '1px solid var(--line)' : 'none',
                  background: 'transparent', textAlign: 'left', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', gap: 10, borderTop: 'none', borderLeft: 'none',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{s.SiteName}</div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{s.SiteId}</div>
                  </div>
                  <span className="chip err">{rojos.missing} {lang === 'es' ? 'rojos' : 'reds'}</span>
                </div>
                <ProgressBar pct={avance.pct} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------- Variant C: data-dense workspace ---------- */

interface SiteRow extends Record<string, unknown> {
  _key: string;
  _pct: number;
}

function VariantC({ role, go, stats, windowState }: ViewProps) {
  const { lang, t } = useI18n();
  const rows: SiteRow[] = stats.sites.map((s) => ({
    ...s,
    _pct: progresoSite(s.SiteId).pct,
    _key: s.SiteId,
  }));

  const strip = [
    { l: t('kpi.orgs'), v: stats.orgs.length, i: ic.org, to: () => go('org') },
    { l: t('kpi.sites'), v: stats.sites.length, i: ic.site, to: () => go('sites') },
    { l: t('kpi.active_instr'), v: stats.instr.filter((i) => !i.disassociated).length, i: ic.instr, to: () => go('instructors') },
    { l: t('kpi.red_pending'), v: stats.sitesWithRed, i: ic.warn, c: 'var(--err)', to: () => go('sites') },
  ];

  const byCountry: Record<string, number> = {};
  stats.sites.forEach((s) => {
    byCountry[s.SiteCountry] = (byCountry[s.SiteCountry] || 0) + 1;
  });
  const maxCountry = Math.max(...Object.values(byCountry), 1);

  const columns: Column<SiteRow>[] = [
    {
      key: 'SiteName',
      label: lang === 'es' ? 'Site' : 'Site',
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 3, height: 22, background: r._pct === 100 ? 'var(--ok)' : r._pct >= 60 ? 'var(--warn)' : 'var(--err)', borderRadius: 2 }} />
          <div>
            <div style={{ fontWeight: 600 }}>{String(r.SiteName)}</div>
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{String(r.SiteId)}</div>
          </div>
        </div>
      ),
    },
    { key: 'OrgName', label: lang === 'es' ? 'Org.' : 'Org.', muted: true },
    { key: 'SiteCity', label: lang === 'es' ? 'Ciudad' : 'City' },
    {
      key: 'SitePostalCode',
      label: 'CP',
      align: 'right',
      render: (r) => {
        const bad = r.SitePostalCode === '0000' || !r.SitePostalCode;
        return <span className={'mono ' + (bad ? 'chip err' : '')}>{String(r.SitePostalCode || '—')}</span>;
      },
    },
    {
      key: 'Workstations',
      label: 'WS',
      align: 'right',
      render: (r) => (
        <span className="mono" style={{ color: r.Workstations ? 'var(--text)' : 'var(--err)', fontWeight: 600 }}>
          {String(r.Workstations || '!')}
        </span>
      ),
    },
    {
      key: 'pct',
      label: t('common.completeness'),
      align: 'right',
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
          <div style={{ width: 64 }}><ProgressBar pct={r._pct} /></div>
          <span className="mono" style={{ fontSize: 11, fontWeight: 700, minWidth: 30, textAlign: 'right', color: r._pct === 100 ? 'var(--ok)' : 'var(--text-2)' }}>
            {r._pct}%
          </span>
        </div>
      ),
    },
  ];

  return (
    <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 14 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* kpi strip */}
        <div className="card" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', padding: 0 }}>
          {strip.map((k, i) => (
            <div
              key={i}
              role="button"
              tabIndex={0}
              onClick={k.to}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  k.to();
                }
              }}
              className="kpi-strip-cell"
              style={{ padding: '12px 14px', borderRight: i < 4 ? '1px solid var(--line)' : 'none', cursor: 'pointer', transition: 'background .15s ease' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: k.c || 'var(--brand-primary)' }}>
                <span style={{ fontSize: 10, color: 'var(--text-2)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{k.l}</span>
                {sized(k.i, 14)}
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{k.v}</div>
            </div>
          ))}
        </div>

        <DataTable columns={columns} rows={rows.slice(0, 12)} onRowClick={(s) => go('site', { siteId: String(s.SiteId) })} />
      </div>

      {/* side rail */}
      <aside style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card" style={{ padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {lang === 'es' ? 'Proceso de actualización' : 'Update process'}
          </div>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: 99, background: windowState.open ? 'var(--ok)' : 'var(--text-3)' }} />
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              {windowState.open ? (lang === 'es' ? 'Abierta' : 'Open') : lang === 'es' ? 'Cerrada' : 'Closed'}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>
            {lang === 'es' ? 'Cierra el' : 'Closes'}{' '}
            <span className="mono" style={{ fontWeight: 600 }}>
              {new Date(windowState.deadline).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          </div>
        </div>

        <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {lang === 'es' ? 'Distribución por país' : 'By country'}
          </div>
          {Object.entries(byCountry)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
            .map(([cc, n]) => {
              const c = COUNTRIES.find((x) => x.code === cc);
              return (
                <div key={cc} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, width: 90, minWidth: 90 }} className="truncate">
                    {c ? (lang === 'es' ? c.name_es : c.name_en) : cc}
                  </span>
                  <div style={{ flex: 1, background: 'var(--bg-2)', borderRadius: 99, height: 6 }}>
                    <div style={{ width: (n / maxCountry) * 100 + '%', background: 'var(--brand-primary)', height: '100%', borderRadius: 99 }} />
                  </div>
                  <span className="mono" style={{ fontSize: 11, fontWeight: 700, minWidth: 18, textAlign: 'right' }}>{n}</span>
                </div>
              );
            })}
        </div>

        {role === 'ETC_ADMIN' ? (
          <div className="card" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
              {lang === 'es' ? 'Pendientes ETC' : 'ETC pending'}
            </div>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>{lang === 'es' ? 'Cambios de contacto legal' : 'Legal contact changes'}</span>
                <span className="mono chip warn">{ds.listLegalRequests().filter((r) => r.status === 'pending').length}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>{lang === 'es' ? 'Instructores desasociados' : 'Disassociated instructors'}</span>
                <span className="mono chip warn">{stats.disassociated}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>{lang === 'es' ? 'CPs con "0000"' : 'Postal codes "0000"'}</span>
                <span className="mono chip err">{stats.sites.filter((s: Site) => s.SitePostalCode === '0000').length}</span>
              </div>
            </div>
          </div>
        ) : null}
      </aside>
    </div>
  );
}

/* ---------- wrapper ---------- */

export function Dashboard() {
  const { lang, t } = useI18n();
  const go = useGo();
  const role = useAppStore((s) => s.role);
  const user = useAppStore((s) => s.user)!;
  const windowState = useAppStore((s) => s.windowState);
  const variant = useAppStore((s) => s.variant.dashboard);
  const setVariantFor = useAppStore((s) => s.setVariantFor);
  const setVariant = (v: string) => setVariantFor('dashboard', v);

  const stats = useScopedData(role, user);

  const isSiteMgr = role === 'SITE_MGR';
  const dashOptions = [
    { value: 'A', es: 'Clásica', en: 'Classic' },
    { value: 'B', es: 'Resumen', en: 'Overview' },
    { value: 'C', es: 'Operativa', en: 'Workspace' },
  ];
  const effVariant = isSiteMgr && variant === 'A' ? 'B' : variant;

  React.useEffect(() => {
    if (isSiteMgr && variant === 'A') setVariant('B');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSiteMgr, variant]);

  const viewProps: ViewProps = { role, user, go, stats, windowState };

  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={t('nav.dashboard')}
        sub={
          role === 'ETC_ADMIN'
            ? lang === 'es' ? 'Vista global · todos los socios' : 'Global view · all partners'
            : lang === 'es' ? 'Vista de tu alcance' : 'Your scope'
        }
        actions={<VariantSwitch value={effVariant} onChange={setVariant} options={dashOptions} exclude={isSiteMgr ? ['A'] : undefined} />}
      />

      {effVariant === 'A' ? <VariantA {...viewProps} /> : effVariant === 'B' ? <VariantB {...viewProps} /> : <VariantC {...viewProps} />}
    </div>
  );
}
